import React, { useEffect, useMemo, useState } from 'react';
import { X, CheckCircle2, UserCheck, Loader2, AlertTriangle, Star } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { tradeLabel } from '@/data/constants';
import ContractorRatingStep from '@/components/ContractorRatingStep';
import { fetchRatingsForListings } from '@/lib/contractorRatings';
import {
  CLOSING_OUTCOMES,
  ClosingOutcome,
  HiringCandidate,
  OFF_PLATFORM,
  closeProject,
  closingErrorMessage,
  loadHiringCandidates,
  outcomeDef,
} from '@/lib/projectClosing';

export interface ClosingTarget {
  id: string;
  title: string;
  status?: string | null;
  hired_buyer_id?: string | null;
  hired_off_platform?: boolean | null;
}

interface Props {
  listing: ClosingTarget | null;
  /** Pre-select an outcome (e.g. 'work_completed' when finishing a hired job). */
  initialOutcome?: ClosingOutcome;
  onClose: () => void;
  /** `rated` is true when the homeowner also submitted a contractor rating. */
  onClosed: (listingId: string, outcome: ClosingOutcome, status: string, rated?: boolean) => void;
}

interface PendingRating {
  contractorId: string;
  name: string;
  status: string;
}

/**
 * Structured project-closing flow for homeowners.
 * Step logic:
 *   1. pick an outcome (5 options; once hired, only "Work completed" and the
 *      not-proceeding outcomes are offered)
 *   2. hired / work completed + at least one purchaser -> contractor selection
 *      is REQUIRED (or an explicit "not from GigGone")
 *   3. optional final job value (hired outcomes) / note (Other)
 *   4. (Phase 1.1) work completed + a GigGone contractor -> inline 1–5 star
 *      rating step (skippable). The project is already saved at this point.
 * The database triggers re-validate everything; the UI only guides.
 */
const ProjectClosingDialog: React.FC<Props> = ({ listing, initialOutcome, onClose, onClosed }) => {
  const { lang } = useI18n();
  const { profile } = useAuth();
  const es = lang === 'es';
  const [outcome, setOutcome] = useState<ClosingOutcome | ''>('');
  const [candidates, setCandidates] = useState<HiringCandidate[]>([]);
  const [loadingCands, setLoadingCands] = useState(false);
  const [selection, setSelection] = useState('');
  const [value, setValue] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [ratingFor, setRatingFor] = useState<PendingRating | null>(null);

  const isHired = listing?.status === 'hired';

  useEffect(() => {
    if (!listing) return;
    setOutcome(initialOutcome || '');
    setSelection(listing.hired_buyer_id || (listing.hired_off_platform ? OFF_PLATFORM : ''));
    setValue('');
    setNote('');
    setError('');
    setRatingFor(null);
    setLoadingCands(true);
    loadHiringCandidates(listing.id)
      .then(setCandidates)
      .finally(() => setLoadingCands(false));
  }, [listing?.id, initialOutcome]);

  // Once the outcome is saved, closing the dialog during the rating step still reports the close.
  const finishRating = (rated: boolean) => {
    if (!listing || !ratingFor) return;
    onClosed(listing.id, 'work_completed', ratingFor.status, rated);
  };
  const dismiss = () => (ratingFor ? finishRating(false) : onClose());

  useEffect(() => {
    if (!listing) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && !saving && dismiss();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  const options = useMemo(
    () => (isHired ? CLOSING_OUTCOMES.filter((o) => o.key !== 'hired_contractor') : CLOSING_OUTCOMES),
    [isHired]
  );

  if (!listing) return null;

  const def = outcomeDef(outcome);
  const needsPick = !!def?.needsContractor && candidates.length > 0;
  const missingPick = needsPick && !selection;
  const missingNote = outcome === 'other' && note.trim().length < 3;
  const canSubmit = !!outcome && !missingPick && !missingNote && !saving && !loadingCands;

  const submit = async () => {
    if (!canSubmit || !outcome) return;
    setSaving(true);
    setError('');
    const parsed = value.trim() ? Number(value.replace(/[^0-9.]/g, '')) : null;
    const res = await closeProject(listing.id, {
      outcome,
      hiredSelection: def?.needsContractor ? selection : '',
      finalJobValue: parsed,
      note,
    });
    if (res.error) {
      setSaving(false);
      setError(closingErrorMessage(res.error, es));
      return;
    }
    // Phase 1.1: offer the structured rating for the hired GigGone contractor.
    const hiredId = outcome === 'work_completed' && selection && selection !== OFF_PLATFORM ? selection : '';
    if (hiredId && profile?.id) {
      const existing = await fetchRatingsForListings([listing.id]).catch(() => new Map());
      setSaving(false);
      if (!existing.has(listing.id)) {
        const name = candidates.find((c) => c.buyer_id === hiredId)?.name || (es ? 'el contratista' : 'the contractor');
        setRatingFor({ contractorId: hiredId, name, status: res.status || 'completed' });
        return;
      }
    }
    setSaving(false);
    onClosed(listing.id, outcome, res.status || '');
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-slate-900/60 p-0 sm:items-center sm:p-4" role="dialog" aria-modal="true" aria-labelledby="close-project-title">
      <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-2xl bg-white shadow-2xl sm:rounded-2xl">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-slate-100 bg-white px-6 py-4">
          <div>
            <h2 id="close-project-title" className="text-lg font-extrabold text-slate-900">
              {ratingFor
                ? es ? 'Califica al contratista' : 'Rate your contractor'
                : isHired ? (es ? 'Actualizar proyecto' : 'Update project') : es ? 'Cerrar proyecto' : 'Close project'}
            </h2>
            <p className="mt-0.5 line-clamp-1 text-sm text-slate-500">{listing.title}</p>
          </div>
          <button onClick={dismiss} disabled={saving} className="rounded-lg p-2 text-slate-500 hover:bg-slate-100" aria-label={es ? 'Cerrar' : 'Close'}>
            <X className="h-5 w-5" />
          </button>
        </div>

        {ratingFor && profile?.id ? (
          <ContractorRatingStep
            es={es}
            listingId={listing.id}
            homeownerId={profile.id}
            contractorId={ratingFor.contractorId}
            contractorName={ratingFor.name}
            onDone={finishRating}
          />
        ) : (
          <>
            <div className="space-y-6 px-6 py-5">
              {/* Step 1 — outcome */}
              <fieldset>
                <legend className="text-sm font-bold text-slate-900">{es ? '¿Cómo terminó este proyecto?' : 'How did this project end?'}</legend>
                <div className="mt-3 grid gap-2">
                  {options.map((o) => (
                    <label
                      key={o.key}
                      className={`flex cursor-pointer items-start gap-3 rounded-xl border p-3.5 transition ${
                        outcome === o.key ? 'border-orange-500 bg-orange-50/60 ring-1 ring-orange-500' : 'border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="closing-outcome"
                        className="mt-1 accent-orange-500"
                        checked={outcome === o.key}
                        onChange={() => { setOutcome(o.key); setError(''); }}
                      />
                      <span>
                        <span className="block text-sm font-bold text-slate-900">{es ? o.es : o.en}</span>
                        <span className="block text-xs text-slate-500">{es ? o.descEs : o.descEn}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </fieldset>

              {/* Step 2 — hired contractor */}
              {def?.needsContractor && (
                <fieldset>
                  <legend className="flex items-center gap-2 text-sm font-bold text-slate-900">
                    <UserCheck className="h-4 w-4 text-orange-500" />
                    {es ? '¿A quién contrataste?' : 'Who did you hire?'}
                    {needsPick && <span className="text-xs font-semibold text-red-600">{es ? '(obligatorio)' : '(required)'}</span>}
                  </legend>
                  {loadingCands ? (
                    <p className="mt-3 flex items-center gap-2 text-sm text-slate-500"><Loader2 className="h-4 w-4 animate-spin" /> {es ? 'Cargando contratistas…' : 'Loading contractors…'}</p>
                  ) : candidates.length === 0 ? (
                    <p className="mt-2 rounded-xl bg-slate-50 px-4 py-3 text-xs text-slate-600">
                      {es
                        ? 'Ningún contratista de GigGone desbloqueó este proyecto, así que no necesitas seleccionar uno.'
                        : 'No GigGone contractor unlocked this project, so no selection is needed.'}
                    </p>
                  ) : (
                    <div className="mt-3 grid gap-2">
                      {candidates.map((c) => (
                        <label
                          key={c.buyer_id}
                          className={`flex cursor-pointer items-center gap-3 rounded-xl border p-3 ${selection === c.buyer_id ? 'border-emerald-500 bg-emerald-50/60 ring-1 ring-emerald-500' : 'border-slate-200 hover:border-slate-300'}`}
                        >
                          <input type="radio" name="hired-contractor" className="accent-emerald-600" checked={selection === c.buyer_id} onChange={() => setSelection(c.buyer_id)} />
                          <span className="min-w-0 flex-1">
                            <span className="block truncate text-sm font-bold text-slate-900">{c.name}</span>
                            <span className="block truncate text-xs text-slate-500">
                              {c.trades.slice(0, 3).map((t) => tradeLabel(t, lang)).join(' · ') || (es ? 'Contratista' : 'Contractor')}
                              {c.unlocked_at ? ` · ${es ? 'desbloqueó' : 'unlocked'} ${new Date(c.unlocked_at).toLocaleDateString()}` : ''}
                            </span>
                          </span>
                        </label>
                      ))}
                      <label className={`flex cursor-pointer items-center gap-3 rounded-xl border border-dashed p-3 ${selection === OFF_PLATFORM ? 'border-slate-500 bg-slate-50' : 'border-slate-300'}`}>
                        <input type="radio" name="hired-contractor" checked={selection === OFF_PLATFORM} onChange={() => setSelection(OFF_PLATFORM)} />
                        <span className="text-sm font-semibold text-slate-700">{es ? 'Alguien que no es de GigGone' : 'Someone not from GigGone'}</span>
                      </label>
                    </div>
                  )}
                </fieldset>
              )}

              {/* Step 3 — optional value / note */}
              {def?.needsContractor && (
                <div>
                  <label htmlFor="final-value" className="text-sm font-bold text-slate-900">
                    {es ? 'Valor aproximado del trabajo (opcional)' : 'Approximate job value (optional)'}
                  </label>
                  <div className="mt-2 flex items-center rounded-xl border border-slate-300 focus-within:border-orange-500">
                    <span className="pl-3 text-sm text-slate-400">$</span>
                    <input id="final-value" inputMode="numeric" value={value} onChange={(e) => setValue(e.target.value.replace(/[^0-9]/g, '').slice(0, 9))} placeholder="12000" className="w-full rounded-xl bg-transparent px-2 py-2.5 text-sm outline-none" />
                  </div>
                  <p className="mt-1 text-xs text-slate-400">{es ? 'Solo para estadísticas internas. Nunca se muestra públicamente.' : 'Used for internal stats only. Never shown publicly.'}</p>
                </div>
              )}

              {outcome && !def?.needsContractor && (
                <div>
                  <label htmlFor="closing-note" className="text-sm font-bold text-slate-900">
                    {outcome === 'other' ? (es ? '¿Qué pasó?' : 'What happened?') : es ? 'Comentario (opcional)' : 'Comment (optional)'}
                  </label>
                  <textarea id="closing-note" value={note} maxLength={500} rows={3} onChange={(e) => setNote(e.target.value)} className="mt-2 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm outline-none focus:border-orange-500" />
                </div>
              )}

              {outcome === 'work_completed' && (
                <p className="flex items-start gap-2 rounded-xl bg-sky-50 px-4 py-3 text-xs text-sky-800">
                  <Star className="mt-0.5 h-4 w-4 shrink-0" />
                  {es
                    ? 'Después de guardar podrás calificar al contratista (1–5 estrellas). También te enviaremos un correo para dejar una breve reseña.'
                    : "After saving you can rate the contractor (1–5 stars). We'll also email you a short review request."}
                </p>
              )}

              {error && (
                <p role="alert" className="flex items-center gap-2 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                  <AlertTriangle className="h-4 w-4 shrink-0" /> {error}
                </p>
              )}
            </div>

            <div className="sticky bottom-0 flex flex-col-reverse gap-2 border-t border-slate-100 bg-white px-6 py-4 sm:flex-row sm:justify-end">
              <button onClick={onClose} disabled={saving} className="rounded-xl border border-slate-300 px-5 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">
                {es ? 'Cancelar' : 'Cancel'}
              </button>
              <button onClick={() => void submit()} disabled={!canSubmit} className="inline-flex items-center justify-center gap-2 rounded-xl bg-orange-500 px-5 py-2.5 text-sm font-bold text-white hover:bg-orange-600 disabled:cursor-not-allowed disabled:opacity-50">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                {missingPick ? (es ? 'Selecciona un contratista' : 'Select a contractor') : es ? 'Guardar resultado' : 'Save outcome'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ProjectClosingDialog;
